import { createSelector } from '@reduxjs/toolkit';
import { selectIsWindowMode } from '@ff-os/pdp-shared-redux/slices/globalSlice';
import { createUndoViewbox } from '@ff-os/rendering-shared/coordinates';
import { Size } from '@ff-os/shared-utils/types/Size';
import { createOptionBindings, selectActiveVariantId } from '@ff-scene-plugins/gift-redux/selectors/variantSelectors';
import { ArPreviewStatus } from '@ff-scene-plugins/gift-redux/slices/uiSlice';
import { GiftPdpState } from '@ff-scene-plugins/gift-redux/store';
import { GiftVariantOptions } from '@ff-scene-plugins/gift-shared/GiftVariantOptions';
import { selectMetaViewbox } from './_redux-meta';
import { selectSceneHeight, selectSceneWidth } from './_redux-scene';
import { selectArPreviewStatus } from './_redux-ui';
import { createBackgroundPaintable } from './preview/helpers/createBackgroundPaintable';
import { createFullPreviewSpaces } from './preview/helpers/createFullPreviewSpaces';

const { createOptionSelector } = createOptionBindings<GiftVariantOptions>();

const selectBackgroundAssetUrlById = createOptionSelector(o => o.backgroundAssetUrl);

const selectVariantPreviewClipById = createOptionSelector(o => o.previewClip);

export const selectVariantPreviewClip = createSelector([
    (state: GiftPdpState) => state, selectActiveVariantId
], selectVariantPreviewClipById);

const selectConfig3dById = createOptionSelector(o => o.config3d);

export const selectConfig3d = createSelector([
    (state: GiftPdpState) => state,
    (state: GiftPdpState) => selectActiveVariantId(state)
], (state: GiftPdpState, id: string) => selectConfig3dById(state, id));

export const selectHasConfig3d = createSelector([
    (state: GiftPdpState) => selectConfig3d(state)
], config3d => config3d !== undefined);

export const selectConfig3dOpenByDefault = createSelector([
    selectConfig3d,
    selectIsWindowMode
], (config3d, isWindowMode) => Boolean(
    config3d && (config3d.openByDefault || isWindowMode)));

export const selectVariantDesignAreaById = createOptionSelector(o => o.designArea);

export const selectActiveVariantDesignArea = createSelector([
    state => state,
    selectActiveVariantId
], (state, variantId) => selectVariantDesignAreaById(state, variantId)!);

export const selectSpacesForFullPreview = createSelector([
    selectSceneWidth,
    selectSceneHeight,
    selectActiveVariantDesignArea,
    (state: GiftPdpState, viewbox: Size) => viewbox
], createFullPreviewSpaces);

const selectUndoViewbox = createSelector([
    (state: GiftPdpState, viewbox: Size) => selectSpacesForFullPreview(state, viewbox)
], createUndoViewbox);

export const selectBackgroundPaintableByVariantId = createSelector([
    selectSceneWidth,
    selectSceneHeight,
    selectUndoViewbox,
    state => selectBackgroundAssetUrlById(state, selectActiveVariantId(state))
], (width, height, undoViewbox, assetUrl) =>
    createBackgroundPaintable(width, height, undoViewbox, assetUrl!));

export const selectSpacesForFullPreview2d = createSelector([
    state => state,
    selectMetaViewbox
], selectSpacesForFullPreview);

export const selectShowArStatus = createSelector([
    selectConfig3d,
    selectArPreviewStatus
], (config3d, arPreviewStatus): ArPreviewStatus => {

    if (!config3d?.isArEnabled) {
        return 'disabled';
    }

    return arPreviewStatus;
});